import type { NumberFieldInputProps, NumberFieldRootProps } from '@base-ui/react';
import type { ReactNode } from 'react';

import { NumberField as BaseNumberField } from '@base-ui/react';
import { useTranslation } from 'react-i18next';

import { useIntl } from '~/hooks/useIntl';
import { clsx, clsxs } from '~/styles/utils';
import { mergeRefs } from '~/utils/mergeRefs';

import { Icon, type IconName } from '../../Icon/Icon';
import { adornmentStyle, controlStateTarget, controlThemeStyle, prefixIconStyle } from '../form.css';
import { useAccessibleAffixes } from '../form.hooks';
import { inputStyle, inputWrapperStyle } from '../Input/Input.css';
import { useInputFocusHandler } from '../Input/Input.hooks';
import { stepperButtonStyle, stepperStyle } from './NumberInput.css';

type Props = Omit<NumberFieldRootProps, 'children' | 'render'> &
  Pick<NumberFieldInputProps, 'autoFocus' | 'inputMode' | 'onBlur' | 'placeholder' | 'ref'> & {
    adornment?: ReactNode;
    icon?: IconName;
    prefix?: string;
    showSteppers?: boolean;
    suffix?: string;
  };

export const NumberInput = ({
  adornment,
  autoFocus,
  className,
  format = { maximumFractionDigits: 4, useGrouping: false },
  icon,
  onBlur,
  placeholder,
  prefix,
  ref,
  showSteppers = false,
  step = 'any',
  suffix,
  ...delegated
}: Props) => {
  const { inputRef, inputWrapperProps } = useInputFocusHandler();
  const { ariaDescribedBy, prefixProps, suffixProps } = useAccessibleAffixes({ prefix, suffix });

  const { locale } = useIntl();
  const { t } = useTranslation(['common']);

  return (
    <BaseNumberField.Root {...delegated} className={clsxs(controlThemeStyle, className)} format={format} locale={locale} step={step}>
      <BaseNumberField.Group className={inputWrapperStyle} {...inputWrapperProps}>
        {icon && <Icon className={prefixIconStyle} name={icon} />}

        {adornment && <span className={adornmentStyle}>{adornment}</span>}

        {prefix && <span {...prefixProps}>{prefix}</span>}

        <BaseNumberField.Input
          autoFocus={autoFocus}
          className={clsx(controlStateTarget, inputStyle)}
          onBlur={onBlur}
          placeholder={placeholder}
          ref={mergeRefs(inputRef, ref)}
          render={(inputProps) => (
            <input
              {...inputProps}
              aria-describedby={[inputProps['aria-describedby'], ariaDescribedBy].filter(Boolean).join(' ') || undefined}
            />
          )}
        />

        {suffix && <span {...suffixProps}>{suffix}</span>}

        {showSteppers && (
          <div className={stepperStyle}>
            <BaseNumberField.Increment aria-label={t('common:fields.numberInput.increment')} className={stepperButtonStyle}>
              <Icon name="Add" size={16} />
            </BaseNumberField.Increment>

            <BaseNumberField.Decrement aria-label={t('common:fields.numberInput.decrement')} className={stepperButtonStyle}>
              <Icon name="Minimize" size={16} />
            </BaseNumberField.Decrement>
          </div>
        )}
      </BaseNumberField.Group>
    </BaseNumberField.Root>
  );
};
